import { useEffect, useMemo, useState } from 'react';
import type { SignatureStrokes } from '../../domain/signature';
import { useT } from '../../i18n/i18n';
import { getMediaAdapter } from '../../native/mediaAdapter';
import { jsPdfRenderer, type PdfRenderer } from '../../report/pdf';
import { ADDENDUM_MAX_PHOTOS, createAddendumAction, missingAddendumFields, nextAddendumNumber, type AddendumField } from '../../state/addendumStore';
import { useStore } from '../../state/createStore';
import { reportStore } from '../../state/reportStore';
import { Button, Field, Icon, InlineMessage, SheetShell, SignaturePad, TextArea, TextInput, showToast } from '../../ui/components';
import { formatDate } from '../../ui/hooks';

// 「补充说明 / Addendum」 form (Owner decision list 2026-10-19 item 27): which item, why, the correction,
// optional photos from the library, the inspector's signature; the date is today's. Each error sits right
// under the field it is about (the form rule, CLAUDE.md §9).
export function AddendumSheet(props: { inspectionId: string; renderer?: PdfRenderer | null; onClose: () => void; onCreated: () => void }) {
  const { t } = useT();
  const reports = useStore(reportStore, (s) => s.reports);
  const number = nextAddendumNumber(props.inspectionId, reports);
  const [item, setItem] = useState('');
  const [reason, setReason] = useState('');
  const [correction, setCorrection] = useState('');
  const [photos, setPhotos] = useState<Blob[]>([]);
  const [signature, setSignature] = useState<SignatureStrokes | null>(null);
  const [errors, setErrors] = useState<AddendumField[]>([]);
  const [photoNote, setPhotoNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photoUrls = useMemo(() => photos.map((blob) => URL.createObjectURL(blob)), [photos]);
  useEffect(() => () => photoUrls.forEach((url) => URL.revokeObjectURL(url)), [photoUrls]);
  const today = useMemo(() => Date.now(), []);

  function clearError(field: AddendumField) {
    setErrors((prev) => prev.filter((f) => f !== field));
  }

  async function addPhotos() {
    setPhotoNote(null);
    try {
      const files = await getMediaAdapter().pickFromLibrary({});
      if (files.length === 0) return;
      const room = ADDENDUM_MAX_PHOTOS - photos.length;
      if (files.length > room) setPhotoNote(t('addendum.photoLimit'));
      setPhotos((prev) => [...prev, ...files.slice(0, Math.max(0, room)).map((f) => f.blob)]);
    } catch (raw) {
      setPhotoNote(raw instanceof Error ? raw.message : String(raw));
    }
  }

  async function create() {
    const missing = missingAddendumFields({ item, reason, correction, signature });
    setErrors(missing);
    setFailure(null);
    if (missing.length > 0) return;
    setBusy(true);
    try {
      const outcome = await createAddendumAction(props.inspectionId, { item, reason, correction, photos, signature }, { renderer: props.renderer ?? jsPdfRenderer });
      if (outcome.ok) {
        showToast(t('addendum.created', { n: outcome.number }));
        props.onCreated();
        return;
      }
      if (outcome.reason === 'invalid') setErrors(outcome.fields);
      else
        setFailure(
          outcome.reason === 'licenceMissing'
            ? t('report.licenceMissing')
            : outcome.reason === 'licenceExpired'
              ? t('report.licenceExpired')
              : t('addendum.failed')
        );
    } finally {
      setBusy(false);
    }
  }

  const fieldError = (field: AddendumField) =>
    errors.includes(field) ? (
      <p className="field-error" role="alert" data-testid={`addendum-${field}-error`}>
        {field === 'signature' ? t('addendum.signRequired') : t('addendum.required')}
      </p>
    ) : null;

  return (
    <SheetShell
      full
      title={`${t('addendum.sheetTitle')} ${number}`}
      subtitle={t('addendum.sheetSubtitle')}
      onClose={props.onClose}
      testId="addendum-sheet"
      footer={
        <>
          {failure ? <InlineMessage kind="err" text={failure} /> : null}
          <Button icon="picture_as_pdf" onClick={() => void create()} disabled={busy} testId="addendum-create">
            {busy ? t('addendum.creating') : t('addendum.create')}
          </Button>
        </>
      }
    >
      <p className="card-note" data-testid="addendum-language-hint">
        {t('addendum.languageHint')}
      </p>
      <Field label={t('addendum.item')} htmlFor="addendum-item">
        <TextInput id="addendum-item" value={item} onChange={(v) => { setItem(v); clearError('item'); }} placeholder={t('addendum.itemPlaceholder')} testId="addendum-item" />
        {fieldError('item')}
      </Field>
      <Field label={t('addendum.reason')} htmlFor="addendum-reason">
        <TextArea id="addendum-reason" value={reason} onChange={(v) => { setReason(v); clearError('reason'); }} placeholder={t('addendum.reasonPlaceholder')} rows={3} testId="addendum-reason" />
        {fieldError('reason')}
      </Field>
      <Field label={t('addendum.correction')} htmlFor="addendum-correction">
        <TextArea id="addendum-correction" value={correction} onChange={(v) => { setCorrection(v); clearError('correction'); }} placeholder={t('addendum.correctionPlaceholder')} rows={5} testId="addendum-correction" />
        {fieldError('correction')}
      </Field>
      <Field label={t('addendum.photos')}>
        {photos.length > 0 ? (
          <div className="addendum-photos" data-testid="addendum-photos">
            {photoUrls.map((url, index) => (
              <div className="addendum-photo" key={url}>
                <img src={url} alt="" />
                <button type="button" aria-label={t('addendum.removePhoto', { n: index + 1 })} onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== index))} data-testid="addendum-photo-remove">
                  <Icon name="close" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {photos.length < ADDENDUM_MAX_PHOTOS ? (
          <Button variant="secondary" icon="photo_library" onClick={() => void addPhotos()} disabled={busy} testId="addendum-add-photos">
            {t('addendum.addPhotos')}
          </Button>
        ) : null}
        {photoNote ? <p className="field-error" role="alert">{photoNote}</p> : null}
      </Field>
      <Field
        label={t('addendum.signature')}
        trailing={
          <button type="button" className="link-inline" onClick={() => setSignature(null)} disabled={signature === null} data-testid="addendum-signature-clear">
            {t('addendum.clearSignature')}
          </button>
        }
      >
        <SignaturePad value={signature} onChange={(next) => { setSignature(next); clearError('signature'); }} clearLabel={t('addendum.clearSignature')} hint={t('addendum.drawHere')} showClear={false} testId="addendum-signature" />
        {fieldError('signature')}
        <p className="addendum-date" data-testid="addendum-date">
          {t('addendum.dateLine', { date: formatDate(today) })}
        </p>
      </Field>
    </SheetShell>
  );
}
